import React, { Dispatch, SetStateAction, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Product } from '../../models/product.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Settings } from '../../models/settings.model';
import { ProductDA } from '../../services/admin/product.service';
import { legendInvalid, legendIconInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { isValidObjectAttributes } from '../../scripts/compare.object.script';
import { suffixLegend } from '../../scripts/name.legend.script';

import AppProductVariantForm from '../../interfaces/main/inventory/product/component/ProductVariantForm';
import AppLoading from '../Loading';
import AppModalSettingCreateUnit from './ModalSettingsCreateUnit';

export interface AppModalProductVariantEditProps {
  companyForUser: Company | null,
  settingsForCompany: Settings | null,
  product?: Product | null,
  idxVariant: number | null,
  variant: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  variants: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]},
  setIdxVariant: Dispatch<SetStateAction<number | null>>,
  setVariant: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>,
  setVariants: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}[], valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}[]}>>,
  loadSettingsForCompany: Function
};

const AppModalProductVariantEdit: React.FunctionComponent<AppModalProductVariantEditProps> = ({companyForUser, settingsForCompany, product, idxVariant, variant, variants, setIdxVariant, setVariant, setVariants, loadSettingsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')

  const resolveUnit = (unitId: string) => {
    const mu = settingsForCompany?.measure_units?.find((m: any) => m.id === unitId)
    return mu ? { id: mu.id, name: mu.name, type: mu.type ?? 'product', symbol: mu.symbol ?? '' } : unitId
  }

  const submitProductVariantEdit = async () => {
    setLoadIndicator('on')

    if (isValidObjectAttributes(variant.valid) ) {
      if (product?.id && companyForUser && variant.value.id) {
        // Update mode with persisted variant: call PUT API
        const variantPayload = {
          ...variant.value,
          unit: resolveUnit(variant.value.unit)
        }

        const response = await ProductDA.putVariant(companyForUser.id, product.id, variant.value.id, variantPayload)

        if (response.status === 200) {
          let tempVariantsValue = variants.value.map((temp_item, temp_index) => {
            if (temp_index === idxVariant) {
              temp_item = variant.value
            }
            return temp_item
          })
          let tempVariantsValid = variants.valid.map((temp_item, temp_index) => {
            if (temp_index === idxVariant) {
              temp_item = variant.valid
            }
            return temp_item
          })

          setLoadIndicator('off')
          setVariants({value: tempVariantsValue, valid: tempVariantsValid})
          executeHideModalProductVariantEdit()
        } else {
          GlobalSwal.fire({
            text: response.data?.message ?? lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          })
          setLoadIndicator('off')
        }
      } else {
        // Create mode or local variant: local state only
        let tempVariantsValue = variants.value.map((temp_item, temp_index) => {
          if (temp_index === idxVariant) {
            temp_item = variant.value
          }
          return temp_item
        })
        let tempVariantsValid = variants.valid.map((temp_item, temp_index) => {
          if (temp_index === idxVariant) {
            temp_item = variant.valid
          }
          return temp_item
        })

        setLoadIndicator('off')
        setVariants({value: tempVariantsValue, valid: tempVariantsValid})
        executeHideModalProductVariantEdit()
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!variant.value.description_short.length) legendIconInvalid('modal-product-variant-edit-input-variant-description-short', 'modal-product-variant-edit-legend-variant-description-short-required')
        if (!variant.value.unit.length) legendInvalid('modal-product-variant-edit-legend-variant-unit-required-edit')
        if (!variant.value.price_sale.length) legendIconInvalid('modal-product-variant-add-input-variant-price-sale', 'modal-product-variant-add-legend-variant-price-sale-required')
        if (!variant.value.price_purchase.length) legendIconInvalid('modal-product-variant-add-input-variant-price-purchase', 'modal-product-variant-add-legend-variant-price-purchase-required')
        if (variant.value.discount.type !== 'none') {
          if (!variant.value.discount.value.length) legendIconInvalid('modal-product-variant-edit-input-variant-discount-value', 'modal-product-variant-edit-legend-variant-discount-value-required')
          if (!variant.value.discount.quantity.length) legendIconInvalid('modal-product-variant-edit-input-variant-discount-quantity', 'modal-product-variant-edit-legend-variant-discount-quantity-required')
        }
        for (let i = 0; i < variant.value.features.length; i++) {
          if (!variant.value.features[i].value) legendInvalid(`modal-product-variant-edit-legend-variant-feature-required${suffixLegend(i)}`)
        }
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalProductVariantEdit = () => {
    modalHide('modal-product-variant-edit')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-description-short', 'modal-product-variant-edit-legend-variant-description-short-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-description-short', 'modal-product-variant-edit-legend-variant-description-short-required')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-description-long', 'modal-product-variant-edit-legend-variant-description-long-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-code', 'modal-product-variant-edit-legend-variant-code-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-sku', 'modal-product-variant-edit-legend-variant-sku-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-codebar', 'modal-product-variant-edit-legend-variant-codebar-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-price-sale', 'modal-product-variant-edit-legend-variant-price-sale-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-price-sale', 'modal-product-variant-edit-legend-variant-price-sale-required')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-price-purchase', 'modal-product-variant-edit-legend-variant-price-purchase-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-price-purchase', 'modal-product-variant-edit-legend-variant-price-purchase-required')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-discount-value', 'modal-product-variant-edit-legend-variant-discount-value-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-discount-value', 'modal-product-variant-edit-legend-variant-discount-value-required')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-discount-quantity', 'modal-product-variant-edit-legend-variant-discount-quantity-valid')
    legendIconValidInvalidRestart('modal-product-variant-edit-input-variant-discount-quantity', 'modal-product-variant-edit-legend-variant-discount-quantity-required')
    legendValidInvalidRestart('modal-product-variant-edit-legend-variant-unit-required')
    legendValidInvalidRestart('modal-product-variant-edit-legend-variant-tax-sale-required')
    legendValidInvalidRestart('modal-product-variant-edit-legend-variant-tax-purchase-required')
    legendValidInvalidRestart('modal-product-variant-edit-legend-date-required')
    legendValidInvalidRestart('modal-product-variant-edit-legend-variant-gallery-max')

    for (let i = 0; i < variant.value.features.length; i++) {
      if (!variant.value.features[i].value) legendInvalid(`modal-product-variant-edit-legend-variant-feature-required${suffixLegend(i)}`)
    }

    setIdxVariant(null)
    setVariant({value: {id: '', description_short: '', description_long: '', code: '', sku: '', codebar: '', price_sale: '', price_purchase: '', tax_code_sale: '', tax_code_purchase: '', tax_rate_sale: 0, tax_rate_purchase: 0, unit: '', discount: {type: 'none', value: '', quantity: ''}, photos: [], features: []}, valid: {description_short: false, description_long: true, code: true, sku: true, codebar: true, price_sale: false, price_purchase: false, tax_sale: false, tax_purchase: false, unit: false, discount: {type: true, value: true, quantity: true}, photos: [], features: [], gallery: true}})
  }

  return (
    <>
      <div id="modal-product-variant-edit" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="text-capitalize">{lang.labels.editProductVariant}</h2>
            </div>
            <div className="modal-body">
              { companyForUser && settingsForCompany
                ?
                <AppProductVariantForm component={"modal-product-variant-edit"} variant={variant} setVariant={setVariant}></AppProductVariantForm>
                :
                <AppLoading></AppLoading>
              }
            </div>
            <div className="modal-footer flex-center">
              <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalProductVariantEdit}>{lang.labels.discard}</button>
              <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitProductVariantEdit}>
                <span className="indicator-label">{lang.labels.saveVariant}</span>
                <span className="indicator-progress">
                  {lang.labels.pleaseWait}
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <AppModalSettingCreateUnit companyForUser={companyForUser} options={{type: "product", main_modal: "modal-product-variant-edit", index: "edit", sub_index: null}} variant={variant} setVariant={setVariant} loadSettingsForCompany={loadSettingsForCompany}></AppModalSettingCreateUnit>
    </>
  )
}

export default AppModalProductVariantEdit;
